import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/common/Navbar";
import { showSuccess } from "../utils/toast";
import baseUrl from "../config/baseUrl";
import dmsApiInventory from "../data/dmsApiInventory";
import TryItOutDialog from "../components/api-reference/TryItOutDialog";
import AuthorizeDialog, { AuthStatusButton, loadApiToken, saveApiToken } from "../components/api-reference/AuthorizeDialog";
import { Tooltip } from "@mui/material";
import "../styles/Users.css";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PhonelinkLockOutlinedIcon from "@mui/icons-material/PhonelinkLockOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import RequestPageOutlinedIcon from "@mui/icons-material/RequestPageOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MarkunreadMailboxOutlinedIcon from "@mui/icons-material/MarkunreadMailboxOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import WebOutlinedIcon from "@mui/icons-material/WebOutlined";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";

// Administration > API Reference: read-only documentation of the DMS backend
// API (active routes in routes/api.php - DMS only, no SugarCRM routes). Data
// lives in src/data/dmsApiInventory.js. The example request is generated from
// each endpoint's real method/URL/auth; no responses are invented.

const PAGE_SIZE = 10;

const MODULE_ICONS = {
  Authentication: LockOutlinedIcon,
  "Webhook Posts": WebhookOutlinedIcon,
  "Users & Roles": GroupOutlinedIcon,
  "Client Document Access (OTP)": PhonelinkLockOutlinedIcon,
  Clients: PeopleAltOutlinedIcon,
  "Linked Contacts": ContactsOutlinedIcon,
  "Document Request Templates": RequestPageOutlinedIcon,
  "Document Requests": AssignmentOutlinedIcon,
  Documents: DescriptionOutlinedIcon,
  "Mail Room": MarkunreadMailboxOutlinedIcon,
  "Document Versions": HistoryOutlinedIcon,
  "Field Settings": TuneOutlinedIcon,
  Contracts: HandshakeOutlinedIcon,
  "Two-Factor Verification": VerifiedUserOutlinedIcon,
  Filters: FilterListOutlinedIcon,
  "Client Portal": WebOutlinedIcon,
  OCR: DocumentScannerOutlinedIcon,
  "Document Upload & Ingestion": CloudUploadOutlinedIcon,
  "Email Templates": DraftsOutlinedIcon,
  "Document Chasing": MarkEmailUnreadOutlinedIcon,
};
const moduleIcon = (name) => MODULE_ICONS[name] || FolderOutlinedIcon;

const METHOD_STYLES = {
  GET: "bg-emerald-50 text-emerald-700 border-emerald-200",
  POST: "bg-blue-50 text-blue-700 border-blue-200",
  PUT: "bg-violet-50 text-violet-700 border-violet-200",
  PATCH: "bg-amber-50 text-amber-700 border-amber-200",
  DELETE: "bg-red-50 text-red-700 border-red-200",
};

// Short badge label + style for each auth value in the inventory.
const AUTH_BADGES = {
  "Bearer token (api guard)": ["Bearer", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  "Middleware auth:sanctum": ["Sanctum", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  "Public (issues access token)": ["Public", "bg-gray-50 text-gray-600 border-gray-200"],
  "None (public)": ["Public", "bg-amber-50 text-amber-700 border-amber-200"],
  "Token read, not enforced": ["Not enforced", "bg-amber-50 text-amber-700 border-amber-200"],
};
const needsToken = (api) => /Bearer|sanctum/i.test(api.auth);

const MethodBadge = ({ method, className = "" }) => (
  <span
    className={`inline-flex min-w-[50px] justify-center rounded border px-1.5 py-px text-[10px] font-semibold tracking-wide ${
      METHOD_STYLES[method] || "bg-gray-50 text-gray-700 border-gray-200"
    } ${className}`}
  >
    {method}
  </span>
);

const Badge = ({ className, title, children }) => (
  <span
    title={title}
    className={`inline-flex items-center whitespace-nowrap rounded border px-1.5 py-px text-[10px] font-medium ${className}`}
  >
    {children}
  </span>
);

const copyText = (text, what) => {
  navigator.clipboard?.writeText(text).then(
    () => showSuccess(`Copied ${what}`),
    () => {},
  );
};

const StatCard = ({ tile, value, label }) => (
  <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
    {tile}
    <div className="min-w-0">
      <div className="text-lg font-semibold leading-tight text-gray-900">{value}</div>
      <div className="truncate text-xs text-gray-500">{label}</div>
    </div>
  </div>
);

const IconTile = ({ icon: Icon, className }) => (
  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${className}`}>
    <Icon sx={{ fontSize: 20 }} />
  </span>
);

// One consistent tile per HTTP method: a tinted tile holding a solid method
// chip in the method's colour (as in the reference design).
const METHOD_TILES = {
  GET: ["bg-emerald-50", "bg-emerald-500"],
  POST: ["bg-blue-50", "bg-blue-600"],
  PUT: ["bg-violet-50", "bg-violet-600"],
  DELETE: ["bg-red-50", "bg-red-500"],
};
const MethodTile = ({ method }) => {
  const [tile, chip] = METHOD_TILES[method];
  return (
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tile}`}>
      <span className={`rounded px-1 py-px text-[8px] font-bold tracking-wide text-white ${chip}`}>{method}</span>
    </span>
  );
};

// Small decorative API illustration for the banner, built from MUI icons
// (hidden on narrower screens so it never crowds the title or search).
const BannerArt = () => (
  <div className="relative hidden h-12 w-40 shrink-0 xl:block" aria-hidden="true">
    <span className="absolute left-2 top-4 flex h-6 w-5 items-center justify-center rounded bg-blue-50 text-blue-500">
      <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />
    </span>
    <span className="absolute left-8 top-0 flex h-6 w-5 items-center justify-center rounded bg-emerald-50 text-emerald-500">
      <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />
    </span>
    <span className="absolute left-7 bottom-0 flex h-5 w-4 items-center justify-center rounded bg-blue-50 text-blue-400">
      <DescriptionOutlinedIcon sx={{ fontSize: 11 }} />
    </span>
    <span className="absolute left-[54px] top-1 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-blue-600 text-[11px] font-bold text-white shadow-md ring-4 ring-sky-100">
      API
    </span>
    <span className="absolute left-[98px] top-2 flex h-9 w-7 items-center justify-center rounded-md bg-blue-600 text-white shadow">
      <DescriptionOutlinedIcon sx={{ fontSize: 17 }} />
    </span>
    <span className="absolute right-1 top-5 flex h-6 w-5 items-center justify-center rounded bg-sky-50 text-sky-500">
      <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />
    </span>
  </div>
);

const EndpointRow = ({ api, selected, onSelect, onTryIt }) => {
  const [authLabel, authClass] = AUTH_BADGES[api.auth] || [api.auth, "bg-gray-50 text-gray-600 border-gray-200"];
  const used = api.usedBy.length > 0;
  return (
    <tr
      onClick={onSelect}
      className={`cursor-pointer border-b border-gray-100 last:border-b-0 ${
        selected ? "bg-nbl-tint" : "hover:bg-gray-50"
      }`}
      aria-selected={selected}
    >
      <td className="px-2 py-1.5 align-middle">
        <MethodBadge method={api.method} />
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 align-middle">
        <code className="font-mono text-xs text-gray-800">{api.endpoint}</code>
        {api.shadowed && (
          <Badge
            className="ml-2 bg-gray-50 text-gray-500 border-gray-200"
            title="Registered again later in routes/api.php with the same method and URI; Laravel uses the later route."
          >
            Shadowed
          </Badge>
        )}
      </td>
      <td className="min-w-[160px] max-w-[260px] px-2 py-1.5 align-middle text-xs leading-snug text-gray-700" title={api.descriptionFull || api.description}>
        {api.handlerMissing ? <span className="text-gray-400">—</span> : api.description}
      </td>
      <td className="px-2 py-1.5 align-middle text-xs leading-snug text-gray-600">
        {api.handler.split("@")[0]}@<wbr />
        {api.handler.split("@").slice(1).join("@")}
      </td>
      <td className="px-2 py-1.5 align-middle">
        {api.handlerMissing ? (
          <Badge
            className="bg-red-50 text-red-700 border-red-200"
            title="The route points to a controller method that does not exist or is commented out - calls to it fail."
          >
            No handler
          </Badge>
        ) : (
          <Badge className={authClass} title={api.auth}>
            {authLabel}
          </Badge>
        )}
      </td>
      <td className="px-2 py-1.5 align-middle">
        <Badge
          className={used ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-500 border-gray-200"}
          title={used ? api.usedBy.join("\n") : "Not called from the DMS UI (backend/external use)"}
        >
          {used ? "Yes" : "No"}
        </Badge>
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-center align-middle">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            copyText(api.endpoint, api.endpoint);
          }}
          className="rounded p-1 text-gray-500 hover:bg-nbl-tint hover:text-nbl-primary"
          title="Copy endpoint"
          aria-label={`Copy ${api.endpoint}`}
        >
          <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
        </button>
        <Tooltip title="Try It Out" arrow>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTryIt();
            }}
            className="ml-0.5 rounded p-1 text-gray-500 hover:bg-nbl-tint hover:text-nbl-primary"
            aria-label={`Try ${api.method} ${api.endpoint}`}
          >
            <PlayArrowOutlinedIcon sx={{ fontSize: 17 }} />
          </button>
        </Tooltip>
      </td>
    </tr>
  );
};

const CodePanel = ({ title, badge, children, onCopy }) => (
  <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5">
    <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs">
      <span className="font-semibold text-gray-900">{title}</span>
      {badge}
    </div>
    <div className="relative min-h-[80px] rounded-md bg-[#0f2739] p-2.5 pr-20">
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          className="absolute right-2 top-2 flex items-center gap-1 rounded-md border border-white/15 bg-white/10 px-2 py-1 text-[11px] text-white/80 hover:bg-white/20"
        >
          <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} /> Copy
        </button>
      )}
      {children}
    </div>
  </div>
);

const buildCurl = (api) =>
  [
    `curl -X ${api.method} \\`,
    `  "${baseUrl}${api.endpoint}" \\`,
    ...(needsToken(api) ? [`  -H "Authorization: Bearer {token}" \\`] : []),
    `  -H "Accept: application/json"`,
  ].join("\n");

const matches = (api, q) =>
  [api.method, api.endpoint, api.description, api.descriptionFull, api.handler, api.auth]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(q);

const DETAIL_TABS = ["Endpoints", "Overview", "UI Usage"];

const ApiReference = () => {
  const { modules, source } = dmsApiInventory;
  const searchRef = useRef(null);

  const [activeModule, setActiveModule] = useState(modules[0]?.name);
  const [detailTab, setDetailTab] = useState(DETAIL_TABS[0]);
  const [search, setSearch] = useState("");
  const [moduleSearch, setModuleSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedKey, setSelectedKey] = useState(null);
  const [tryApi, setTryApi] = useState(null);
  // Page-wide Bearer token for Try It Out (sessionStorage only).
  const [apiToken, setApiToken] = useState(loadApiToken);
  const [authOpen, setAuthOpen] = useState(false);
  const updateApiToken = (t) => {
    setApiToken(t);
    saveApiToken(t);
  };

  // Ctrl/Cmd + K focuses the API search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const allApis = useMemo(() => modules.flatMap((m) => m.apis), [modules]);
  const methodCount = (method) => allApis.filter((a) => a.method === method).length;

  const q = search.trim().toLowerCase();
  const filteredModules = useMemo(
    () => modules.map((m) => ({ ...m, shown: q ? m.apis.filter((a) => matches(a, q)) : m.apis })),
    [modules, q],
  );

  // While searching, jump to a module that has matches if the current one has none.
  useEffect(() => {
    if (!q) return;
    const current = filteredModules.find((m) => m.name === activeModule);
    if (!current?.shown.length) {
      const firstHit = filteredModules.find((m) => m.shown.length);
      if (firstHit) setActiveModule(firstHit.name);
    }
  }, [q, filteredModules, activeModule]);

  useEffect(() => {
    setPage(1);
    setSelectedKey(null);
  }, [activeModule, q]);

  const current = filteredModules.find((m) => m.name === activeModule) || filteredModules[0];
  const shown = current ? current.shown : [];
  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const pageApis = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const keyOf = (api) => `${api.method} ${api.endpoint} ${api.handler}`;
  const selected = shown.find((a) => keyOf(a) === selectedKey) || pageApis[0];

  const mq = moduleSearch.trim().toLowerCase();
  const sidebarModules = filteredModules.filter((m) => !mq || m.name.toLowerCase().includes(mq));

  const ModuleIcon = current ? moduleIcon(current.name) : FolderOutlinedIcon;
  const controllers = current ? [...new Set(current.apis.map((a) => a.handler.split("@")[0]))] : [];
  const usedCount = current ? current.apis.filter((a) => a.usedBy.length).length : 0;
  const tally = (list, fn) => {
    const counts = {};
    for (const item of list) counts[fn(item)] = (counts[fn(item)] || 0) + 1;
    return Object.entries(counts);
  };

  return (
    // Fixed-height page (like the other DMS pages): the shell never scrolls.
    // Navbar, banner, stat cards, the Modules sidebar and the selected module's
    // header/tabs stay put; only the module's endpoint content scrolls. Below
    // the lg breakpoint the body scrolls normally instead.
    <div className="user-container" style={{ overflowY: "hidden" }}>
      <div className="navbar">
        <Navbar />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-white px-3 pb-3 pt-3 md:px-4 lg:overflow-hidden">
        {/* Compact banner: icon, title, subtitle, illustration, search */}
        <div className="flex shrink-0 flex-col gap-3 rounded-lg border border-gray-200 bg-gradient-to-r from-white via-white to-sky-50 px-3 py-2 md:flex-row md:items-center">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-nbl-dark text-white">
              <CodeOutlinedIcon sx={{ fontSize: 22 }} />
            </span>
            <div className="min-w-0">
              <h1 className="text-base font-semibold leading-tight text-gray-900">API Reference</h1>
              <p className="text-xs font-medium leading-tight text-nbl-primary">DMS Backend API Documentation</p>
              <p className="truncate text-xs text-gray-500" title={`Source: ${source}`}>
                Browse and explore all {allApis.length} DMS APIs with example requests, descriptions, and details.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 md:ml-auto">
            <BannerArt />
            <div className="relative w-full md:w-[300px]">
              <SearchOutlinedIcon
                sx={{ fontSize: 18 }}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search APIs, endpoints, description..."
                aria-label="Search APIs"
                className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-16 text-[13px] text-gray-800 outline-none placeholder:text-gray-400 focus:border-nbl-primary focus:ring-2 focus:ring-nbl-tint"
              />
              <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-gray-200 bg-gray-50 px-1 py-px font-sans text-[10px] text-gray-500 sm:block">
                Ctrl + K
              </kbd>
            </div>
            <AuthStatusButton token={apiToken} onClick={() => setAuthOpen(true)} />
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <StatCard
            tile={<IconTile icon={DescriptionOutlinedIcon} className="bg-nbl-tint text-nbl-primary" />}
            value={allApis.length}
            label="Total Endpoints"
          />
          <StatCard
            tile={<IconTile icon={LayersOutlinedIcon} className="bg-sky-50 text-sky-600" />}
            value={modules.length}
            label="Modules"
          />
          {["GET", "POST", "PUT", "DELETE"].map((method) => (
            <StatCard key={method} tile={<MethodTile method={method} />} value={methodCount(method)} label={`${method} Endpoints`} />
          ))}
        </div>

        {/* Module list (fixed) + selected module (its content scrolls) */}
        <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1 lg:flex-row">
          <aside className="flex w-full shrink-0 flex-col rounded-lg border border-gray-200 bg-white p-2 lg:min-h-0 lg:w-[250px]">
            <div className="shrink-0 px-2 pb-2 pt-1">
              <h2 className="text-sm font-semibold text-gray-900">Modules</h2>
              <p className="text-[11px] text-gray-500">Select a module to view its API endpoints</p>
            </div>
            <div className="relative mx-1 mb-2 shrink-0">
              <SearchOutlinedIcon
                sx={{ fontSize: 16 }}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="search"
                value={moduleSearch}
                onChange={(e) => setModuleSearch(e.target.value)}
                placeholder="Search modules..."
                aria-label="Search modules"
                className="h-8 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2 text-xs text-gray-800 outline-none placeholder:text-gray-400 focus:border-nbl-primary focus:ring-2 focus:ring-nbl-tint"
              />
            </div>
            <ul className="flex max-h-64 flex-col overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1">
              {sidebarModules.length === 0 && (
                <li className="px-3 py-3 text-xs text-gray-500">No modules match “{moduleSearch}”.</li>
              )}
              {sidebarModules.map((m) => {
                const Icon = moduleIcon(m.name);
                const active = m.name === current?.name;
                const empty = q && !m.shown.length;
                return (
                  <li key={m.name} className="border-b border-gray-100 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setActiveModule(m.name)}
                      aria-current={active ? "true" : undefined}
                      className={`flex w-full items-center gap-2 border-l-[3px] px-2 py-1.5 text-left text-xs transition-colors ${
                        active
                          ? "border-nbl-primary bg-nbl-tint font-medium text-nbl-primary"
                          : `border-transparent hover:bg-gray-50 ${empty ? "text-gray-400" : "text-gray-700"}`
                      }`}
                    >
                      <Icon sx={{ fontSize: 16 }} className={active ? "text-nbl-primary" : "text-gray-500"} />
                      <span className="min-w-0 flex-1 truncate" title={m.name}>
                        {m.name}
                      </span>
                      <span
                        className={`shrink-0 rounded-full px-1.5 py-px text-[10px] ${
                          active ? "bg-white text-nbl-primary" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {m.shown.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {current && (
            <section className="flex min-w-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white lg:min-h-0">
              {/* Module header + tabs: fixed */}
              <div className="flex shrink-0 items-center gap-2.5 px-3 py-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-nbl-tint text-nbl-primary">
                  <ModuleIcon sx={{ fontSize: 18 }} />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-sm font-semibold leading-tight text-gray-900">{current.name}</h2>
                  <p className="truncate text-[11px] text-gray-500">
                    {current.apis.length} endpoint{current.apis.length === 1 ? "" : "s"} · {usedCount} called by the DMS UI
                  </p>
                </div>
                <span className="shrink-0 rounded-full border border-teal-200 bg-nbl-tint px-2 py-px text-[11px] font-medium text-nbl-primary">
                  {current.apis.length} Endpoint{current.apis.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="flex shrink-0 gap-4 border-b border-gray-200 px-3" role="tablist">
                {DETAIL_TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={detailTab === tab}
                    onClick={() => setDetailTab(tab)}
                    className={`-mb-px border-b-2 pb-1.5 text-xs ${
                      detailTab === tab
                        ? "border-nbl-primary font-medium text-nbl-primary"
                        : "border-transparent text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* The only vertically scrolling region */}
              <div className="overflow-y-auto lg:min-h-0 lg:flex-1">
                {detailTab === "Endpoints" && (
                  <>
                    {shown.length ? (
                      <div className="overflow-x-auto px-3 pt-2">
                        <table className="w-full min-w-[820px] table-auto border-separate border-spacing-0 text-left">
                          <thead>
                            <tr className="text-[11px] text-gray-600">
                              <th className="rounded-l-md bg-gray-50 px-2 py-1.5 font-medium">Method</th>
                              <th className="bg-gray-50 px-2 py-1.5 font-medium">Endpoint</th>
                              <th className="bg-gray-50 px-2 py-1.5 font-medium">Description</th>
                              <th className="bg-gray-50 px-2 py-1.5 font-medium">Controller</th>
                              <th className="bg-gray-50 px-2 py-1.5 font-medium">Auth</th>
                              <th className="whitespace-nowrap bg-gray-50 px-2 py-1.5 font-medium">Used in UI</th>
                              <th className="rounded-r-md bg-gray-50 px-2 py-1.5 text-center font-medium">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageApis.map((api) => (
                              <EndpointRow
                                key={keyOf(api)}
                                api={api}
                                selected={selected && keyOf(api) === keyOf(selected)}
                                onSelect={() => setSelectedKey(keyOf(api))}
                                onTryIt={() => setTryApi(api)}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="p-6 text-center text-xs text-gray-500">
                        No endpoints in {current.name} match “{search}”.
                      </p>
                    )}
                    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs text-gray-500">
                      <span>
                        {shown.length
                          ? `Showing ${(page - 1) * PAGE_SIZE + 1} - ${Math.min(page * PAGE_SIZE, shown.length)} of ${shown.length} endpoint${shown.length === 1 ? "" : "s"}`
                          : "Showing 0 endpoints"}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 disabled:opacity-40"
                          aria-label="Previous page"
                        >
                          <ChevronLeftIcon sx={{ fontSize: 16 }} />
                        </button>
                        {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setPage(n)}
                            aria-current={n === page ? "page" : undefined}
                            className={`h-7 min-w-[28px] rounded-md px-2 text-xs ${
                              n === page ? "bg-nbl-primary text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                          disabled={page === pageCount}
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 disabled:opacity-40"
                          aria-label="Next page"
                        >
                          <ChevronRightIcon sx={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </div>

                    {/* Example request + details for the selected endpoint */}
                    {selected && (
                      <div className="grid gap-3 px-3 pb-3 xl:grid-cols-2">
                        <CodePanel
                          title="Example Request"
                          badge={
                            <>
                              <MethodBadge method={selected.method} />
                              <code className="min-w-0 truncate font-mono text-xs text-gray-600">{selected.endpoint}</code>
                            </>
                          }
                          onCopy={() => copyText(buildCurl(selected), "request")}
                        >
                          <pre className="overflow-x-auto whitespace-pre font-mono text-[11px] leading-5 text-[#9ee6f0]">
                            {buildCurl(selected)}
                          </pre>
                        </CodePanel>
                        <CodePanel
                          title="Endpoint Details"
                          badge={
                            <span className="flex items-center gap-1 text-[11px] text-gray-500">
                              <InfoOutlinedIcon sx={{ fontSize: 14 }} /> from the backend source
                            </span>
                          }
                        >
                          <dl className="grid grid-cols-[90px_1fr] gap-x-3 gap-y-1 font-mono text-[11px] leading-5">
                            <dt className="text-white/50">handler</dt>
                            <dd className="text-[#9ee6f0] [overflow-wrap:anywhere]">{selected.handler}</dd>
                            <dt className="text-white/50">auth</dt>
                            <dd className="text-[#fcd9a8]">{selected.handlerMissing ? "No handler (route fails)" : selected.auth}</dd>
                            <dt className="text-white/50">description</dt>
                            <dd className="text-white/90">{selected.descriptionFull || selected.description || "—"}</dd>
                            <dt className="text-white/50">used in UI</dt>
                            <dd className="text-white/90 [overflow-wrap:anywhere]">
                              {selected.usedBy.length ? selected.usedBy.map((f) => <span key={f} className="block">src/{f}</span>) : "not called from the DMS UI"}
                            </dd>
                          </dl>
                        </CodePanel>
                      </div>
                    )}
                  </>
                )}

                {detailTab === "Overview" && (
                  <div className="grid gap-3 p-3 md:grid-cols-3">
                    <div className="rounded-lg border border-gray-200 p-2.5">
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Methods</div>
                      <div className="flex flex-wrap gap-2">
                        {tally(current.apis, (a) => a.method).map(([m, n]) => (
                          <span key={m} className="flex items-center gap-1.5 text-xs text-gray-700">
                            <MethodBadge method={m} /> {n}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-2.5">
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Authentication</div>
                      <ul className="flex flex-col gap-1 text-xs text-gray-700">
                        {tally(current.apis, (a) => (a.handlerMissing ? "No handler" : a.auth)).map(([label, n]) => (
                          <li key={label} className="flex justify-between gap-2">
                            <span>{label}</span>
                            <span className="text-gray-500">{n}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-2.5">
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Controllers</div>
                      <ul className="flex flex-col gap-1 text-xs text-gray-700">
                        {controllers.map((c) => (
                          <li key={c} className="flex justify-between gap-2">
                            <span className="[overflow-wrap:anywhere]">{c}</span>
                            <span className="text-gray-500">{current.apis.filter((a) => a.handler.startsWith(`${c}@`)).length}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {detailTab === "UI Usage" && (
                  <ul className="flex flex-col divide-y divide-gray-100 px-3 py-2">
                    {current.apis.map((api) => (
                      <li key={keyOf(api)} className="flex flex-col gap-1 py-1.5 md:flex-row md:items-start md:gap-4">
                        <span className="flex min-w-0 items-center gap-2 md:w-[45%]">
                          <MethodBadge method={api.method} />
                          <code className="truncate font-mono text-xs text-gray-800" title={api.endpoint}>
                            {api.endpoint}
                          </code>
                        </span>
                        <span className="min-w-0 flex-1 text-xs text-gray-600">
                          {api.usedBy.length ? (
                            api.usedBy.map((f) => (
                              <span key={f} className="block [overflow-wrap:anywhere]">
                                src/{f}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400">Not called from the DMS UI</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
      <TryItOutDialog
        api={tryApi}
        open={!!tryApi}
        onClose={() => setTryApi(null)}
        apiToken={apiToken}
        onAuthorize={() => setAuthOpen(true)}
      />
      <AuthorizeDialog open={authOpen} token={apiToken} onClose={() => setAuthOpen(false)} onSave={updateApiToken} />
    </div>
  );
};

export default ApiReference;
